import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Result, Spin } from 'antd';
import { CreditCardOutlined, LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { authHeader } from '../../../utils/api';

const { Title, Paragraph, Text } = Typography;

const DatasetPayment = () => {
  const [searchParams] = useSearchParams();
  const datasetId = searchParams.get('dataset_id');
  const datasetName = searchParams.get('dataset_name');
  const amount = parseFloat(searchParams.get('amount')) || 0;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [dataset, setDataset] = useState(null);
  const [fetchingDataset, setFetchingDataset] = useState(true);

  useEffect(() => {
    const fetchDatasetDetails = async () => {
      try {
        const response = await fetch('/api/audience-datasets/me', {
          headers: { ...authHeader() }
        });
        
        if (response.ok) {
          const data = await response.json();
          const found = data.items.find(d => d.id === parseInt(datasetId));
          setDataset(found || null);
        }
      } catch (error) {
        console.error('Error fetching dataset:', error);
      } finally {
        setFetchingDataset(false);
      }
    };

    if (datasetId) {
      fetchDatasetDetails();
    } else {
      setFetchingDataset(false);
    }
  }, [datasetId]);

  const handlePayment = async (values) => {
    setLoading(true);
    try {
      // Process payment
      const paymentData = {
        dataset_id: datasetId,
        amount: amount,
        card_last4: values.cardNumber.slice(-4),
        cardholder_name: values.cardholderName,
        timestamp: new Date().toISOString(),
      };

      // In production, send to your payment gateway API
      // const response = await fetch('/api/payments/process', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json', ...authHeader() },
      //   body: JSON.stringify(paymentData)
      // });

      console.log('Payment processed:', paymentData);

      // Mark payment as successful
      setPaymentSuccess(true);
      message.success('Payment successful! Preparing your download...');

      // Trigger download immediately
      setTimeout(() => {
        triggerDownload();
      }, 500);

    } catch (error) {
      message.error('Payment failed. Please try again.');
      console.error('Payment error:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerDownload = async () => {
    try {
      console.log(`Attempting to download dataset ${datasetId}...`);
      const response = await fetch(`/api/audience-datasets/${datasetId}/export`, {
        headers: { ...authHeader() }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        
        let errorMessage = 'Download failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch (e) {
          // Not JSON, use default message
        }
        
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      console.log('Download blob size:', blob.size);
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${datasetName || 'dataset'}_${datasetId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      message.success('Download started! You can close this window.');
    } catch (error) {
      console.error('Download error:', error);
      message.error(error.message || 'Download failed. Please contact support.');
    }
  };

  const validateCardNumber = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter card number'));
    }
    // Remove spaces and check if it's 16 digits
    const cleaned = value.replace(/\s/g, '');
    if (!/^\d{16}$/.test(cleaned)) {
      return Promise.reject(new Error('Card number must be 16 digits'));
    }
    return Promise.resolve();
  };

  const validateExpiry = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter expiry date'));
    }
    if (!/^\d{2}\/\d{2}$/.test(value)) {
      return Promise.reject(new Error('Format must be MM/YY'));
    }
    return Promise.resolve();
  };

  const validateCVV = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter CVV'));
    }
    if (!/^\d{3,4}$/.test(value)) {
      return Promise.reject(new Error('CVV must be 3 or 4 digits'));
    }
    return Promise.resolve();
  };

  if (fetchingDataset) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!datasetId || !dataset) {
    return (
      <div style={{ padding: '48px', maxWidth: '600px', margin: '0 auto' }}>
        <Result
          status="error"
          title="Invalid Dataset"
          subTitle="The dataset you're trying to access doesn't exist or you don't have permission."
        />
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div style={{ padding: '48px', maxWidth: '600px', margin: '0 auto' }}>
        <Result
          status="success"
          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          title="Payment Successful!"
          subTitle={
            <div>
              <p>Your payment has been processed successfully.</p>
              <p>Your download should start automatically. If it doesn't, you can close this window and try downloading again from your dashboard.</p>
            </div>
          }
          extra={[
            <Button type="primary" key="close" onClick={() => window.close()}>
              Close Window
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '48px 24px', maxWidth: '600px', margin: '0 auto' }}>
      <Card>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <CreditCardOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <Title level={2}>Secure Payment</Title>
          <Paragraph type="secondary">
            Complete your payment to download the dataset
          </Paragraph>
        </div>

        <Card 
          style={{ marginBottom: '24px', background: '#f6f8fa' }}
          bodyStyle={{ padding: '16px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Text strong>Dataset:</Text>
            <Text>{datasetName || `Dataset #${datasetId}`}</Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Text strong>Records:</Text>
            <Text>{dataset.row_count?.toLocaleString() || 0} rows</Text>
          </div>
          <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: '12px', marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: '16px' }}>Total Amount:</Text>
              <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>${amount.toFixed(2)}</Text>
            </div>
          </div>
        </Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={handlePayment}
          autoComplete="off"
        >
          <Form.Item
            label="Cardholder Name"
            name="cardholderName"
            rules={[{ required: true, message: 'Please enter cardholder name' }]}
          >
            <Input 
              size="large" 
              placeholder="John Doe"
              prefix={<CreditCardOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="Card Number"
            name="cardNumber"
            rules={[{ validator: validateCardNumber }]}
          >
            <Input 
              size="large" 
              placeholder="1234 5678 9012 3456"
              maxLength={19}
              onChange={(e) => {
                // Auto-format card number with spaces
                let value = e.target.value.replace(/\s/g, '');
                let formattedValue = value.match(/.{1,4}/g)?.join(' ') || value;
                form.setFieldsValue({ cardNumber: formattedValue });
              }}
            />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              label="Expiry Date"
              name="expiryDate"
              rules={[{ validator: validateExpiry }]}
            >
              <Input 
                size="large" 
                placeholder="MM/YY"
                maxLength={5}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length >= 2) {
                    value = value.slice(0, 2) + '/' + value.slice(2, 4);
                  }
                  form.setFieldsValue({ expiryDate: value });
                }}
              />
            </Form.Item>

            <Form.Item
              label="CVV"
              name="cvv"
              rules={[{ validator: validateCVV }]}
            >
              <Input 
                size="large" 
                placeholder="123"
                maxLength={4}
                type="password"
                prefix={<LockOutlined />}
              />
            </Form.Item>
          </div>

          <Form.Item style={{ marginTop: '24px', marginBottom: 0 }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large" 
              block
              loading={loading}
              icon={<LockOutlined />}
            >
              {loading ? 'Processing Payment...' : `Pay $${amount.toFixed(2)} & Download`}
            </Button>
          </Form.Item>

          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <LockOutlined /> Your payment information is secure and encrypted
            </Text>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default DatasetPayment;
